import { supabase } from '@/integrations/supabase/client';

export interface ProfilePhotoUploadResponse {
  success: boolean;
  url?: string;
  error?: string;
}

export interface ProfilePhotoUploadOptions {
  file: File;
  onProgress?: (progress: number) => void;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

export const validateImageFile = (file: File): { isValid: boolean; error?: string } => {
  // Check file type
  if (!ALLOWED_TYPES.includes(file.type)) {
    return {
      isValid: false,
      error: 'File format not supported. Please use JPG, JPEG, or PNG.'
    };
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: 'Too large. Only images below 10MB are accepted.'
    };
  }

  return { isValid: true };
};

export const uploadProfilePhoto = async (
  options: ProfilePhotoUploadOptions
): Promise<ProfilePhotoUploadResponse> => {
  const { file, onProgress } = options;

  try {
    // Validate file
    const validation = validateImageFile(file);
    if (!validation.isValid) {
      return {
        success: false,
        error: validation.error
      };
    }

    // Get current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError || !session || !session.user) {
      throw new Error('User not authenticated');
    }

    const user = session.user;

    // Generate unique filename
    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}-${Date.now()}.${fileExt}`;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileName', fileName);

    const { data: uploadData, error: uploadError } = await supabase.functions.invoke('upload-profile-photo', {
      body: formData,
      headers: {
        'Authorization': `Bearer ${session.access_token}`
      }
    });

    if (uploadError) {
      throw new Error('Failed to upload image. Please try again.');
    }

    if (!uploadData.success) {
      throw new Error(uploadData.error || 'Failed to upload image. Please try again.');
    }

    // Update user profile with new photo URL
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ 
        profile_picture_url: uploadData.url,
        profile_picture_updated_at: new Date().toISOString()
      })
      .eq('id', user.id);

    if (updateError) {
      throw new Error('Failed to update profile. Please try again.');
    }

    return {
      success: true,
      url: uploadData.url
    };

  } catch (error) {
    console.error('Profile photo upload error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to upload image. Please try again.'
    };
  }
};

export const deleteProfilePhoto = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    // Get current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session || !session.user) {
      throw new Error('User not authenticated');
    }
    
    const user = session.user;

    // Get current profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('profile_picture_url')
      .eq('id', user.id)
      .single();

    if (profileError) {
      throw new Error('Failed to get profile');
    }

    if (profile.profile_picture_url) {
      // Extract file path from URL
      const url = new URL(profile.profile_picture_url);
      const filePath = url.pathname.split('/').slice(-2).join('/'); // Get last two parts

      // Delete from storage
      const { error: deleteError } = await supabase.storage
        .from('profile-photos')
        .remove([filePath]);

      if (deleteError) {
        console.warn('Failed to delete old photo from storage:', deleteError);
      }
    }

    // Update profile to remove photo URL
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ 
        profile_picture_url: null,
        profile_picture_updated_at: new Date().toISOString()
      })
      .eq('id', user.id);

    if (updateError) {
      throw new Error('Failed to update profile');
    }

    return { success: true };

  } catch (error) {
    console.error('Delete profile photo error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete photo'
    };
  }
};

export const cleanupOldProfilePhotos = async (): Promise<{ success: boolean; deletedCount?: number; error?: string }> => {
  try {
    // Get current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session || !session.user) {
      throw new Error('User not authenticated');
    }

    // Call the cleanup Edge Function
    const { data, error } = await supabase.functions.invoke('cleanup-old-profile-photos', {
      headers: {
        'Authorization': `Bearer ${session.access_token}`
      }
    });

    if (error) {
      throw new Error('Failed to cleanup old photos');
    }

    return {
      success: data.success,
      deletedCount: data.deletedCount,
      error: data.error
    };

  } catch (error) {
    console.error('Cleanup old photos error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to cleanup old photos'
    };
  }
};
